import {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useId,
  useCallback,
  cloneElement,
  isValidElement,
} from 'react'
import { createPortal } from 'react-dom'

import { useApp } from '../../../app/AppContext.jsx'

import styles from './Tooltip.module.scss'

const EDGE_MARGIN  = 16   // px buffer between bubble and viewport edge
const TRIGGER_GAP  = 10   // px between trigger and bubble
const ENTER_DELAY  = 200  // ms hover dwell before showing
const ARROW_INSET  = 14   // min px the arrow stays from a bubble edge

// Fallback chain for auto-flip when preferred placement does not fit.
// Same-axis flip first (top↔bottom, left↔right), then perpendicular axis.
const FALLBACK_CHAIN = {
  top:    ['top',    'bottom', 'right',  'left'],
  bottom: ['bottom', 'top',    'right',  'left'],
  right:  ['right',  'left',   'bottom', 'top'],
  left:   ['left',   'right',  'bottom', 'top'],
}

/**
 * Tooltip — generic, portal-rendered tooltip wrapper.
 *
 * Wraps any element/component and reveals `title` on hover or keyboard focus.
 * Renders into the current app instance portal host so it escapes overflow:hidden
 * ancestors.
 *
 * **Placement** (`'top' | 'bottom' | 'right' | 'left'`): the requested side
 * is preferred; if the bubble does not fit, the component walks a fallback
 * chain (same-axis flip first, then perpendicular axis). Once placed, the
 * bubble is clamped along the cross axis with `EDGE_MARGIN` buffer and the
 * arrow stays pointing at the trigger center.
 *
 * **Adaptive width:** `max-width: min(<cap>, calc(100vw − 2·EDGE_MARGIN))`,
 * so the bubble shrinks on narrow screens without overflowing.
 *
 * **Mobile** (`isMobile` from `useApp()`) is suppressed by default — there is
 * no hover. Pass `disableOnMobile={false}` to keep it (tap-to-toggle).
 *
 * **Conditional rendering:** pass `disabled={!cond}` (or leave `title` empty)
 * to render children untouched — no portal, no listeners, no extra DOM.
 * Lets callers gate the tooltip on a flag without conditional JSX.
 *
 * Children may be any single React element (cloned with hover/focus handlers)
 * or arbitrary fragments/text (wrapped in a `display: contents` span).
 *
 * @param {object}              props
 * @param {React.ReactNode}     props.title              — tooltip body; string, JSX, anything
 * @param {React.ReactNode}     props.children           — trigger
 * @param {'top'|'bottom'|'right'|'left'} [props.placement='top'] — preferred side; auto-flips if no room
 * @param {boolean}            [props.disabled=false]    — short-circuit: render children only, no tooltip
 * @param {boolean}            [props.disableOnMobile]   — suppress on mobile layouts (default true)
 * @param {number|string}      [props.maxWidth]          — cap bubble width (px number or any CSS length)
 * @param {boolean}            [props.noWrap]            — single-line bubble (no text wrapping)
 * @param {number}             [props.enterDelay]        — ms hover dwell before opening (default 200)
 * @param {object}             [props.bubbleStyle]       — inline style merged onto the bubble
 * @param {string}             [props.bubbleClassName]   — extra class for the bubble
 */
export function Tooltip({
  title,
  children,
  placement       = 'top',
  disabled        = false,
  disableOnMobile = true,
  maxWidth,
  noWrap          = false,
  enterDelay      = ENTER_DELAY,
  bubbleStyle,
  bubbleClassName,
}) {
  const app      = useApp()
  const isMobile = !!app.isMobile

  const triggerElRef = useRef(null)    // measured DOM node of the trigger
  const bubbleRef    = useRef(null)
  const enterTimer   = useRef(null)
  const id           = useId()

  const [open, setOpen]       = useState(false)
  const [entered, setEntered] = useState(false)  // drives pop-in animation
  const [pos, setPos]         = useState(null)   // {top,left,arrowX|arrowY,placement}

  const enabled =
    !disabled &&
    title != null &&
    title !== '' &&
    !(disableOnMobile && isMobile)

  // ── Position computation ────────────────────────────────────────────────
  const computePosition = useCallback(() => {
    const trig   = triggerElRef.current
    const bubble = bubbleRef.current
    if (!trig || !bubble) return

    const trigRect = trig.getBoundingClientRect()
    const vw       = window.innerWidth
    const vh       = window.innerHeight

    // CSS clamps bubble width / height; just measure the result.
    const bRect = bubble.getBoundingClientRect()
    const w     = bRect.width
    const h     = bRect.height

    const fits = (side) => {
      switch (side) {
        case 'top':    return trigRect.top         >= h + TRIGGER_GAP + EDGE_MARGIN
        case 'bottom': return vh - trigRect.bottom >= h + TRIGGER_GAP + EDGE_MARGIN
        case 'right':  return vw - trigRect.right  >= w + TRIGGER_GAP + EDGE_MARGIN
        case 'left':   return trigRect.left        >= w + TRIGGER_GAP + EDGE_MARGIN
        default:       return false
      }
    }

    // Pick first side from the fallback chain that fits; if none fit, keep requested.
    const chain  = FALLBACK_CHAIN[placement] ?? FALLBACK_CHAIN.top
    const actual = chain.find(fits) ?? placement

    let top, left, arrowX = null, arrowY = null

    if (actual === 'top' || actual === 'bottom') {
      top = actual === 'top'
        ? trigRect.top - h - TRIGGER_GAP
        : trigRect.bottom + TRIGGER_GAP

      const triggerCenterX = trigRect.left + trigRect.width / 2
      left = triggerCenterX - w / 2
      const minLeft = EDGE_MARGIN
      const maxLeft = vw - EDGE_MARGIN - w
      if (maxLeft >= minLeft) left = Math.max(minLeft, Math.min(left, maxLeft))
      else                    left = minLeft

      arrowX = Math.max(ARROW_INSET, Math.min(triggerCenterX - left, w - ARROW_INSET))
    } else {
      // 'left' / 'right'
      left = actual === 'right'
        ? trigRect.right + TRIGGER_GAP
        : trigRect.left - w - TRIGGER_GAP

      const triggerCenterY = trigRect.top + trigRect.height / 2
      top = triggerCenterY - h / 2
      const minTop = EDGE_MARGIN
      const maxTop = vh - EDGE_MARGIN - h
      if (maxTop >= minTop) top = Math.max(minTop, Math.min(top, maxTop))
      else                  top = minTop

      arrowY = Math.max(ARROW_INSET, Math.min(triggerCenterY - top, h - ARROW_INSET))
    }

    setPos({ top, left, arrowX, arrowY, placement: actual })
  }, [placement])

  // Measure & position once the bubble mounts; re-measure on resize/scroll.
  useLayoutEffect(() => {
    if (!open) return
    computePosition()
  }, [open, computePosition, title])

  useEffect(() => {
    if (!open) return
    // Trigger pop-in transition on the next frame.
    const raf = requestAnimationFrame(() => setEntered(true))

    const recompute = () => computePosition()
    const dismiss   = () => closeNow()
    window.addEventListener('resize', recompute)
    window.addEventListener('scroll', dismiss, true)
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', recompute)
      window.removeEventListener('scroll', dismiss, true)
    }
  }, [open, computePosition])

  // ── Open / close handlers ───────────────────────────────────────────────
  const openSoon = (target) => {
    if (!enabled) return
    if (target) triggerElRef.current = target
    clearTimeout(enterTimer.current)
    enterTimer.current = setTimeout(() => setOpen(true), enterDelay)
  }
  const closeNow = () => {
    clearTimeout(enterTimer.current)
    setEntered(false)
    setOpen(false)
  }

  useEffect(() => () => clearTimeout(enterTimer.current), [])

  // ── Short-circuit when disabled — render children untouched ─────────────
  if (!enabled) return children ?? null

  // ── Trigger wrapping ────────────────────────────────────────────────────
  const handlers = {
    onMouseEnter: (e) => openSoon(e.currentTarget),
    onMouseLeave: closeNow,
    onFocus:      (e) => openSoon(e.currentTarget),
    onBlur:       closeNow,
  }

  let trigger
  if (isValidElement(children)) {
    const child = children
    trigger = cloneElement(child, {
      onMouseEnter: (e) => { child.props.onMouseEnter?.(e); handlers.onMouseEnter(e) },
      onMouseLeave: (e) => { child.props.onMouseLeave?.(e); handlers.onMouseLeave(e) },
      onFocus:      (e) => { child.props.onFocus?.(e);      handlers.onFocus(e) },
      onBlur:       (e) => { child.props.onBlur?.(e);       handlers.onBlur(e) },
      'aria-describedby': open ? id : child.props['aria-describedby'],
    })
  } else {
    // Fallback: wrap in a contents-span so we don't disturb the parent layout.
    trigger = (
      <span className={styles.contentsWrap} {...handlers} aria-describedby={open ? id : undefined}>
        {children}
      </span>
    )
  }

  // ── Render ──────────────────────────────────────────────────────────────
  const portalTarget = typeof document !== 'undefined'
    ? document.getElementById(app.portalHostId)
    : null

  const actualPlacement = pos?.placement ?? placement

  const inlineBubbleStyle = {
    // Until we've measured, render off-screen but renderable (so we *can* measure).
    top:  pos?.top  ?? -9999,
    left: pos?.left ?? -9999,
    '--tooltip-arrow-x': pos?.arrowX != null ? `${pos.arrowX}px` : '50%',
    '--tooltip-arrow-y': pos?.arrowY != null ? `${pos.arrowY}px` : '50%',
    ...(maxWidth != null
      ? { maxWidth: typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth }
      : null),
    ...bubbleStyle,
  }

  return (
    <>
      {trigger}
      {open && portalTarget && createPortal(
        <div
          ref={bubbleRef}
          id={id}
          role="tooltip"
          className={[
            styles.bubble,
            styles[`placement_${actualPlacement}`],
            entered && pos ? styles.entered : '',
            noWrap ? styles.noWrap : '',
            bubbleClassName ?? '',
          ].filter(Boolean).join(' ')}
          style={inlineBubbleStyle}
        >
          {title}
          <span className={styles.arrow} aria-hidden="true" />
        </div>,
        portalTarget,
      )}
    </>
  )
}
